import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocationStore } from '@/store/location';
import { useAuth } from '@/contexts/AuthContext';
import { restaurantService, type Restaurant } from '@/services/restaurant';
import { productService, type ProductOffer } from '@/services/product';
import { favoritesService } from '@/services/favorites';
import { reviewService } from '@/services/review';
import { calculateDistance } from '@/lib/distance';
import { useRealtimeRestaurantStock } from '@/hooks/useRealtimeStock';
import type { Product, OrderType } from '@/pages/store/store-detail.types';
import { getKoreanDate } from '@/lib/dateUtils';

interface UseStoreDetailProps {
  storeId: string | undefined;
  initialOrderType?: OrderType;
  readonly?: boolean;
  showToast: (message: string) => void;
}

type DeadlineOfferCandidate = {
  offer_type?: ProductOffer['offer_type'];
  is_active?: ProductOffer['is_active'];
  valid_from?: string | null;
  valid_until?: string | null;
};

const isDeadlineDiscountOfferActiveToday = (
  offer: DeadlineOfferCandidate,
  today: string,
  now: Date
) => {
  if (offer.offer_type !== 'DEADLINE_DISCOUNT' || offer.is_active !== true) {
    return false;
  }

  if (offer.valid_from) {
    const offerDate = offer.valid_from.includes('T')
      ? offer.valid_from.split('T')[0]
      : offer.valid_from;
    if (offerDate !== today) {
      return false;
    }
  }

  if (offer.valid_until) {
    const validUntil = new Date(offer.valid_until);
    if (!Number.isNaN(validUntil.getTime()) && now > validUntil) {
      return false;
    }
  }

  return true;
};

const hasActiveDeadlineOfferToday = (
  offers: Array<DeadlineOfferCandidate | undefined> | undefined,
  today: string,
  now: Date
) => {
  if (!offers || offers.length === 0) {
    return false;
  }

  return offers.some(
    (offer) => offer && isDeadlineDiscountOfferActiveToday(offer, today, now)
  );
};

export function useStoreDetail({ storeId, initialOrderType, readonly = false, showToast }: UseStoreDetailProps) {
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { user } = useAuth();
  
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrderType, setSelectedOrderType] = useState<OrderType>(initialOrderType || 'DEADLINE_DISCOUNT');
  const [isFavorite, setIsFavorite] = useState(false);
  const [isCheckingFavorite, setIsCheckingFavorite] = useState(false);
  const [hidingProducts, setHidingProducts] = useState<Set<string>>(new Set());
  const [calculatedRating, setCalculatedRating] = useState<number>(0);
  const [calculatedTotalReviews, setCalculatedTotalReviews] = useState<number>(0);
  const [isLoadingReviewStats, setIsLoadingReviewStats] = useState(false);
  const isFetchingDataRef = useRef(false);
  const isCheckingFavoriteRef = useRef(false);

  // 리뷰 통계 계산 함수
  const calculateReviewStats = async (restaurantId: string) => {
    try {
      setIsLoadingReviewStats(true);
      const reviews = await reviewService.getReviewsByRestaurant(restaurantId);
      
      // getReviewsByRestaurant는 이미 active 리뷰만 반환하고 평점도 변환됨 (DB 1-10 → UI 0.5-5.0)
      const totalReviews = reviews.length;
      const avgRating = totalReviews > 0 
        ? reviews.reduce((sum, review) => sum + review.rating, 0) / totalReviews 
        : 0;
      
      setCalculatedTotalReviews(totalReviews);
      setCalculatedRating(avgRating);
      
      // 디버깅용 콘솔 로그
      console.log('Review stats calculated:', { restaurantId, totalReviews, avgRating, reviews });
    } catch (error) {
      console.error('Failed to calculate review stats:', error);
      setCalculatedTotalReviews(0);
      setCalculatedRating(0);
    } finally {
      setIsLoadingReviewStats(false);
    }
  };

  // 매장 정보 로드
  useEffect(() => {
    const fetchStoreData = async () => {
      if (!storeId || isFetchingDataRef.current) return;
      
      try {
        isFetchingDataRef.current = true;
        setIsLoading(true);
        
        // 매장 정보 조회
        const restaurantResult = await restaurantService.getRestaurantById(storeId);
        if (restaurantResult.success && restaurantResult.data) {
          setRestaurant(restaurantResult.data);
          // 리뷰 통계 계산
          await calculateReviewStats(storeId);
        }
        
        // 상품 목록 조회
        const productsList = await productService.getProductsByRestaurant(storeId);
        if (productsList && productsList.length > 0) {
          // Convert service Products to local Product interface
          // Store all offers for each product, don't filter by single offer type
          const todayKST = getKoreanDate();
          const now = new Date();
          const localProducts: Product[] = productsList.map(product => {
            // Get the current offer based on initial order type, fallback to first offer
            const currentOrderType = initialOrderType || 'DEADLINE_DISCOUNT';
            const currentOffer = product.offers?.find(offer => offer.offer_type === currentOrderType) || product.offers?.[0];
            const deadlineOfferAvailableToday =
              currentOrderType === 'DEADLINE_DISCOUNT'
                ? hasActiveDeadlineOfferToday(product.offers, todayKST, now)
                : true;
            
            return {
              id: product.id,
              name: product.name,
              description: product.description,
              category: product.category,
              original_price: product.original_price,
              discounted_price: currentOffer?.discounted_price || product.original_price,
              discount_rate: currentOffer?.discount_rate || 0,
              discount_type: currentOffer?.offer_type || 'REGULAR_ORDER',
              stock_quantity: product.stock?.[0]?.current_stock || 0,
              max_order_quantity: currentOffer?.max_quantity_per_order || 999,
              is_available: product.is_available,
              image_url: product.image_url,
              ingredients: product.ingredients,
              allergens: product.allergens,
              calories: product.calories,
              // Keep all offers for dynamic pricing
              allOffers: product.offers || [],
              deadlineOfferAvailableToday
            };
          });
          setProducts(localProducts);
        }
      } catch (error) {
        console.error('Failed to fetch store data:', error);
      } finally {
        setIsLoading(false);
        isFetchingDataRef.current = false;
      }
    };

    fetchStoreData();
  }, [storeId]); // storeId만 의존성으로 설정

  // 거리 계산 (매장 로드 후 한 번만 실행)
  useEffect(() => {
    if (restaurant && currentLocation?.coordinate && 
        currentLocation.coordinate.latitude !== 0 && 
        currentLocation.coordinate.longitude !== 0 &&
        restaurant.latitude && restaurant.longitude) {
      // Distance calculation available but not used in current design
      calculateDistance(
        currentLocation.coordinate.latitude,
        currentLocation.coordinate.longitude,
        restaurant.latitude,
        restaurant.longitude
      );
    }
  }, [restaurant?.id, currentLocation?.coordinate?.latitude, currentLocation?.coordinate?.longitude]); // 좌표가 실제로 변경될 때만 계산

  // 즐겨찾기 상태 확인
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!storeId || isCheckingFavoriteRef.current) return;
      
      try {
        isCheckingFavoriteRef.current = true;
        setIsCheckingFavorite(true);
        const result = await favoritesService.isFavorite(user?.id || null, storeId);
        if (result.success) {
          setIsFavorite(result.data || false);
        }
      } catch (error) {
        console.error('Failed to check favorite status:', error);
      } finally {
        setIsCheckingFavorite(false);
        isCheckingFavoriteRef.current = false;
      }
    };

    checkFavoriteStatus();
  }, [user?.id, storeId]);

  // 실시간 재고 업데이트 구독
  useRealtimeRestaurantStock(storeId || null, {
    onStockUpdate: (productId, newStock, isAvailable) => {
      setProducts(prev => prev.map(product => 
        product.id === productId 
          ? { ...product, stock_quantity: newStock, is_available: Boolean(isAvailable) }
          : product
      ));
    },
    onOutOfStock: (productId, productName) => {
      // 품절 상품 숨김 처리
      setHidingProducts(prev => new Set(prev).add(productId));
      showToast(`${productName}이(가) 품절되었습니다`);
      
      // 1초 후 완전히 제거
      setTimeout(() => {
        setProducts(prev => prev.filter(p => p.id !== productId));
        setHidingProducts(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      }, 1000);
    },
    onBackInStock: (productId, productName) => {
      // 재입고 시 숨김 해제
      setHidingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
      showToast(`${productName}이(가) 재입고되었습니다`);
    }
  });

  // Update product pricing when order type changes
  useEffect(() => {
    const todayKST = getKoreanDate();
    const now = new Date();
    setProducts(prevProducts => 
      prevProducts.map(product => {
        // Find the offer for the selected order type
        const selectedOffer = product.allOffers?.find(offer => offer.offer_type === selectedOrderType);
        const deadlineOfferAvailableToday =
          selectedOrderType === 'DEADLINE_DISCOUNT'
            ? hasActiveDeadlineOfferToday(product.allOffers, todayKST, now)
            : true;
        
        if (selectedOffer) {
          return {
            ...product,
            discounted_price: selectedOffer.discounted_price,
            discount_rate: selectedOffer.discount_rate,
            discount_type: selectedOffer.offer_type,
            max_order_quantity: selectedOffer.max_quantity_per_order || 999,
            deadlineOfferAvailableToday
          };
        } else {
          // If no offer for this order type, show original price
          return {
            ...product,
            discounted_price: product.original_price,
            discount_rate: 0,
            discount_type: selectedOrderType,
            max_order_quantity: 999,
            deadlineOfferAvailableToday
          };
        }
      })
    );
  }, [selectedOrderType]);

  // Show all products regardless of order type, but with appropriate pricing
  const filteredProducts = products.filter(product => {
    // Show all products, but check if there's an offer for the selected order type
    const hasOfferForType = product.allOffers?.some(offer => offer.offer_type === selectedOrderType);
    // Show the product if it has an offer for this type, or if it's a basic product without specific offers
    return hasOfferForType || !product.allOffers || product.allOffers.length === 0;
  }); // 품절 상품도 표시 (회색으로 처리)

  // 즐겨찾기 토글
  const handleFavoriteToggle = async () => {
    if (!storeId || isCheckingFavorite || readonly) return;
    
    // 로그인 상태 확인 (Supabase 저장을 위해 필요)
    if (!user) {
      showToast('로그인이 필요합니다');
      navigate('/login');
      return;
    }
    
    try {
      setIsCheckingFavorite(true);
      const result = await favoritesService.toggleFavorite(user.id, storeId);
      
      if (result.success) {
        const newIsFavorite = !isFavorite;
        setIsFavorite(newIsFavorite);
        
        if (newIsFavorite) {
          showToast('즐겨찾기에 추가되었습니다');
        } else {
          showToast('즐겨찾기에서 제거되었습니다');
        }
      } else {
        showToast(result.error || '즐겨찾기 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Favorite toggle error:', error);
      showToast('즐겨찾기 처리 중 오류가 발생했습니다');
    } finally {
      setIsCheckingFavorite(false);
    }
  };

  return {
    // State
    restaurant,
    products: filteredProducts,
    isLoading,
    selectedOrderType,
    isFavorite,
    isCheckingFavorite,
    hidingProducts,
    calculatedRating,
    calculatedTotalReviews,
    isLoadingReviewStats,
    
    // Actions
    setSelectedOrderType,
    handleFavoriteToggle
  };
}
